import {
  OAuthProvider,
  signInWithPopup,
  updateProfile,
  User,
  getAdditionalUserInfo,
} from "firebase/auth";
import {
  initFirebase,
  clearFirebaseLocalStorage,
} from "@core/services/api/firebase";
import { apiRequest } from "@core/services/api/apiRequest";
import type { ClaimedVia } from "@core/features/invitation";

export async function registerUserWithOAuth(
  provider: OAuthProvider,
  inviteCode: string | null,
  claimedVia?: ClaimedVia | null,
  onError?: () => void,
  _retried = false
) {
  let firebaseUser: User | null = null;
  let isNewUser = false;

  try {
    // Ensure firebase is initialized
    const auth = await initFirebase();

    // Sign in or register user with OAuth provider
    const result = await signInWithPopup(auth, provider);
    firebaseUser = result.user;

    // Check if this is a new user (just created) vs existing user signing in
    const additionalInfo = getAdditionalUserInfo(result);
    isNewUser = additionalInfo?.isNewUser ?? false;

    // Get user's name from the provider's data
    const firstName = firebaseUser.displayName?.split(" ")[0] || "";
    const lastName =
      firebaseUser.displayName?.split(" ").slice(1).join(" ") || "";

    // Update user profile with the parsed name
    await updateProfile(firebaseUser, {
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
    });

    // Get Firebase token
    const idToken = await firebaseUser.getIdToken();

    // Prepare API request body
    const body = {
      firebase_token: idToken,
      email: firebaseUser.email,
      first_name: firstName,
      last_name: lastName,
      is_new_firebase_user: isNewUser,
      ...(inviteCode && { invite_code: inviteCode }),
      ...(claimedVia && { claimed_via: claimedVia }),
    };

    // Make API call to backend
    const response = await apiRequest("/invitation/signup/", "POST", body);

    return { authToken: idToken, ...response };
  } catch (error) {
    const msg = (error as Error).message || "";
    const authError = error as any;
    const email = authError.customData?.email || authError.email;

    // If an orphaned Firebase account from a different provider blocks OAuth,
    // try to clean it up via prevalidate and retry once.
    if (
      !_retried &&
      email &&
      msg.includes("auth/account-exists-with-different-credential")
    ) {
      let prevalidateResult;
      try {
        prevalidateResult = await apiRequest(
          "/auth/prevalidate/",
          "POST",
          { email },
          false
        );
      } catch {
        // Graceful degradation — prevalidate failed, fall through to error
      }

      if (prevalidateResult?.cleaned) {
        return registerUserWithOAuth(
          provider,
          inviteCode,
          claimedVia,
          onError,
          true
        );
      }
      if (prevalidateResult?.retry_after) {
        throw new Error("Please wait a few minutes and try again.");
      }
    }

    clearFirebaseLocalStorage();
    onError?.();
    throw error;
  }
}
